import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { EntityByIdResponse } from '@risksmart-app/trpc/types';
import type { GetEntityByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useIsFeatureVisibleToOrg } from 'src/utils/featureFlags';
import { useTRPC } from 'src/utils/trpc';

const mapTrpcEntityByIdToGraphQL = (
  trpcData?: EntityByIdResponse
): GetEntityByIdQuery => {
  return { entity_by_pk: trpcData };
};

export const useGetEntityByIdTRPC = (id?: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.entity.getById.queryOptions({ id: id! }),
    gcTime: 60,
    staleTime: 0,
    enabled: trpcEnabled && !skip && !!id, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcEntityByIdToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
